import { isNotNullish } from 'effect/Predicate'

import { CORE_CONSTANT } from '../../../../constant.ts'
import { SALESCORE_USER_WITH_GROUP_MODEL } from '../../../../constant/models/SALESCORE_USER_WITH_GROUP_MODEL.ts'
import { PRESET_MODELS } from '../../../../constant/presetModels.ts'
import type { CoreModel } from '../../../../schemas/model/model.ts'
import type { ModelProperty } from '../../../../schemas/model/modelProperty.ts'
import type { ViewUiDimensionCandidate } from '../../../../schemas/ui/dimension.ts'
import type { Metric } from '../../../../schemas/view_config/index.ts'
import { flatNodes } from '../../../query/executeViewQuery/util.ts'
import { PROPERTY_DIMENSION_LENGTH_LIMIT_WITH_TIMEFRAME } from '../../../query/types.ts'
import { makeIdentifiable } from '../../../util/makeIdentifiable.ts'
import type { CompileContext } from '../../common.ts'
import { pruneInvalidMultiplicityNode } from '../sheet/compileSheetAndMeasure.ts'
import { compileSheetViewConfigTree } from '../sheet/compileSheetViewConfigTree.ts'

// dimensionにできるのは、measureNodeに対して多重度が適切になるフィールドのみ

export function generateUiDimensionCandidates(metric: Metric, context: CompileContext): ViewUiDimensionCandidate[] {
  const { sheet, measure } = metric
  const { tree } = sheet
  if (tree === undefined) {
    return []
  }
  const queryTree = compileSheetViewConfigTree(tree, context)
  if (queryTree === undefined) {
    return []
  }

  // カスタムSQLの時の集計ノードは、一旦先頭のノード扱いとする
  const measureNodeName =
    measure.type === 'kpiPreset' ? measure.property.nodeName : (measure.dependentNodeNames[0] ?? tree.name)
  const nodes = flatNodes(queryTree)
  const measureNode = nodes.find((x) => x.name === measureNodeName)
  // measureNodeが見つからないことはないはずだが、万が一見つからなかったら一旦ルートとする
  const prunedTree = pruneInvalidMultiplicityNode(queryTree, measureNode ?? queryTree) ?? queryTree
  const prunedFlatNodeNames = new Set(flatNodes(prunedTree).map((x) => x.name))
  // pruneした後のnodeに含まれるfieldのみとする
  const dimensions = (sheet.fields ?? [])
    .filter((configField) => prunedFlatNodeNames.has(configField.property.nodeName))
    .flatMap((configField): ViewUiDimensionCandidate[] => {
      const model = context.modelSearcher.searchModel(configField.property.modelName)
      const property = model?.properties.find((y) => y.name === configField.property.propertyName)

      //
      if (model === undefined || property === undefined) {
        return [
          // モデル、プロパティが見つからなかったら一旦非表示とする
          // {
          //   type: 'fieldName',
          //   label: [x.property.nodeName, x.property.propertyName].join('_'),
          //   fieldName,
          // },
        ]
      }
      // 一旦雑に実装したが、fieldNameをkey扱いしている箇所があり、動かない
      // if (property.type === 'date' || property.type === 'datetime') {
      //   const timeframes: ViewTimeframe[] = ['day', 'week', 'month']
      //   return timeframes.map(
      //     (timeframe): ViewUiDimensionCandidate => ({
      //       type: 'fieldName',
      //       label: [[model.label, property.label].join(': '), timeframe].join('_'),
      //       fieldName,
      //       dateSpan: timeframe,
      //     }),
      //   )
      // }

      return [getPropertyDimension({ nodeName: configField.property.nodeName, model, property })]
    })
  const userGroupCategoryNames =
    context.resources.organizationSetting.userGroupCategoryNames.length > 0
      ? context.resources.organizationSetting.userGroupCategoryNames
      : CORE_CONSTANT.USER_GROUP_CATEGORY_NAMES
  return [
    ...(metric.user === undefined
      ? []
      : [
          CORE_CONSTANT.KPI_PIVOT_USER_DIMENSION(),
          ...userGroupCategoryNames.map(
            (userGroupCategoryName, index): ViewUiDimensionCandidate =>
              getSalescoreUserGroupDimension(index + 1, userGroupCategoryName),
          ),
        ]),
    // KPIとしてはDATE_DIMENSIONをいれる必要性はない。KPI_PIVOT側は、compileKpiPivotViewConfig側でdimensionsの指定をしているので、ここで定義しなくて良い。
    // （ユーザーに関しても同じだが、ユーザーグループがあるのでユーザーは紐付けしている）
    // -> 2022/11/09 この仕様やめるか？dimensionsにdateがないと、KPIビュー側でやりづらい
    metric.date === undefined ? undefined : CORE_CONSTANT.KPI_PIVOT_KPI_DATE_DIMENSION(),
    ...dimensions,
  ].filter(isNotNullish)
}

function generateSalescoreUserGroupNodeName(depth: number) {
  return `${PRESET_MODELS.USER_GROUP.NAME}_d${depth}`
}

export function getSalescoreUserGroupDimension(depth: number, userGroupCategoryName: string): ViewUiDimensionCandidate {
  return {
    type: 'property',
    key: [`p`, generateSalescoreUserGroupNodeName(depth), `id`].join('_'),
    property: {
      nodeName: SALESCORE_USER_WITH_GROUP_MODEL.name,
      modelName: SALESCORE_USER_WITH_GROUP_MODEL.name,
      propertyName: `user_group_d${depth}_id`,
    },
    label: userGroupCategoryName,
    fieldType: 'string',
  }
}

export function getPropertyDimension({
  nodeName,
  model,
  property,
}: {
  nodeName: string
  model: CoreModel
  property: ModelProperty
}): ViewUiDimensionCandidate {
  const isDateProperty = property.type === 'date' || property.type === 'datetime'
  const lengthLimit = isDateProperty ? PROPERTY_DIMENSION_LENGTH_LIMIT_WITH_TIMEFRAME : undefined

  return {
    type: 'property',
    key: makeIdentifiable([`p`, nodeName, property.name].join('_'), lengthLimit),
    property: {
      nodeName,
      modelName: model.name,
      propertyName: property.name,
    },
    label: [model.label, property.label].join(': '),
    fieldType: property.type,
  }
}
